// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The navigation model for the whole app, as data.
 *
 * Bygning først (PLAN.md decisions 1 and 8): the case is one tile shell with
 * five modes (`MODES`, from `bygning/modes.ts`) — the top bar's tabs — and a
 * handful of expert pages (`EXPERT_ENTRIES`) inside a minimal `ToolFrame`,
 * reached from the ⌘K palette and Modelindstillinger. The Sidebar and its
 * Værktøjer group are retired.
 *
 * Kept free of React so the contract is unit-testable in Node.
 */

import type { Health, ProjectSummary } from '../api/types';
import { MODES, type ModeDef } from '../bygning/modes';
import { editorKeyAction } from './editorKeys';
import type { TargetKind } from './keyTargets';
import {
  INDBERETNING_PATH,
  INDSTILLINGER_PATH,
  KORTLAEGNING_PATH,
  OVERBLIK_PATH,
  PROJEKTDATA_PATH,
  RAPPORT_PATH,
  REDIGER_PATH,
  RESSOURCER_PATH,
  SAGSDATA_PATH,
  SEGMENTERING_PATH,
  SKABELONER_PATH,
} from './links';

export { MODES };
export type { ModeDef };

/** One expert page: a route inside the ToolFrame. */
export interface ExpertEntry {
  to: string;
  label: string;
  /** What it is for, in the palette's second column. */
  hint: string;
  /** Extra words the ⌘K filter matches (not shown). */
  keywords?: string;
}

/**
 * The expert routes, unchanged paths (decision 1). Order: the scan pipeline
 * first, then its inspectors, then the case-level tools.
 */
export const EXPERT_ENTRIES: readonly ExpertEntry[] = [
  { to: '/pipeline', label: 'Pipeline', hint: 'Scan' },
  { to: '/pipeline/log', label: 'Kørselslog', hint: 'Scan' },
  // Posegraf moved to Rediger › Scan & pose (frontend-u0o.1); /graph-view redirects there.
  { to: '/frames', label: 'Billeder', hint: 'Scan' },
  { to: '/geometry', label: 'Geometri', hint: 'Scan' },
  { to: '/instances', label: 'Instanser', hint: 'Scan' },
  { to: '/labels', label: 'Labels', hint: 'Scan' },
  { to: SEGMENTERING_PATH, label: 'Segmentering', hint: 'Scan' },
  { to: SKABELONER_PATH, label: 'Skabeloner', hint: 'Sag' },
  { to: INDSTILLINGER_PATH, label: 'Indstillinger', hint: 'Sag' },
];

/** The ToolFrame title for an expert path (or a path nested under one). */
export function expertEntryFor(pathname: string): ExpertEntry | null {
  // Longest match first, so /pipeline/log is Kørselslog, not Pipeline.
  const sorted = [...EXPERT_ENTRIES].sort((a, b) => b.to.length - a.to.length);
  return sorted.find((e) => pathname === e.to || pathname.startsWith(`${e.to}/`)) ?? null;
}

/**
 * The case list. Absolute and outside every case's router: link to it with a
 * plain `<a href>` (a page load), never a router link, which would resolve it
 * inside the case (`/sager/<cid>/sager`). Same as `CASES_PATH` in `cases.ts`.
 */
export const ALL_CASES_PATH = '/sager';

/**
 * The shell's phone breakpoint (Phase 6 R5): 900px, in rem like the existing
 * `45rem` query in controls.module.css. CSS cannot import it, so the
 * `@media` rules in the Bygning tiles and top bar spell the same value.
 */
export const DRAWER_QUERY = '(max-width: 56.25rem)';

/**
 * What a key does to an open drawer or sheet. Esc closes it from anywhere in
 * the shell except a text field, where R10 gives Esc to the field (revert its
 * draft).
 */
export function drawerKeyAction(k: { key: string; kind: TargetKind; open: boolean }): 'close' | null {
  if (!k.open) return null;
  return editorKeyAction({ key: k.key, kind: k.kind }) === 'close' ? 'close' : null;
}

/**
 * Whether a click inside the drawer closes it: any link does, the link to the
 * page already shown included, since the user has chosen where to be. `tags`
 * are the tag names from the click target up to the drawer.
 */
export function drawerClickCloses(tags: readonly string[]): boolean {
  return tags.some((t) => t.toUpperCase() === 'A');
}

/** An old path that still resolves, so bookmarks and cross-links keep working. */
export interface Redirect {
  from: string;
  to: string;
}

/**
 * Retired paths, rendered by App.tsx as `<Navigate replace>` ahead of the
 * catch-all, so they never stack history. The query is dropped. A source
 * must not also be a `<Route>` in App.tsx. The redirects that keep part of
 * the query or set their own (`/kortlaegning`, `/indberetning`, `/viewport`,
 * `/sagsdata`) are `QUERY_REDIRECTS` below, not listed here.
 */
export const REDIRECTS: readonly Redirect[] = [
  { from: '/on-site', to: RESSOURCER_PATH },
  { from: '/onsite', to: RESSOURCER_PATH },
  { from: '/materials', to: RESSOURCER_PATH },
  { from: '/export', to: RAPPORT_PATH },
  // Projektdata's inventory closed Overblik (Kortlægning fixes spec A2).
  { from: PROJEKTDATA_PATH, to: OVERBLIK_PATH },
];

/** A retired path that keeps some of its query when it redirects. */
export interface QueryRedirect {
  from: string;
  to: string;
  /** Query keys carried over (any other key is dropped), or `'all'` to keep the whole query. */
  keep: readonly string[] | 'all';
  /** Query set on the target regardless of the source's. */
  set?: Readonly<Record<string, string>>;
}

export const QUERY_REDIRECTS: readonly QueryRedirect[] = [
  { from: KORTLAEGNING_PATH, to: RESSOURCER_PATH, keep: ['type'] },
  { from: INDBERETNING_PATH, to: RAPPORT_PATH, keep: [], set: { page: 'indberetning' } },
  // Decision 8: the Ground absorbs the viewport's deep links.
  { from: '/viewport', to: OVERBLIK_PATH, keep: ['pano', 'captureFor'] },
  // The Sagsdata mode folded into Overblik: its editor opens as a dialog there.
  { from: SAGSDATA_PATH, to: OVERBLIK_PATH, keep: [], set: { edit: 'sag' } },
  // Posegraf became Rediger › Scan & pose (frontend-u0o.1); its deep links (edge, pair, Blokke) keep working.
  { from: '/graph-view', to: `${REDIGER_PATH}/scan`, keep: 'all' },
];

/** The target of a query-keeping redirect, query included (`/ressourcer?type=3`). */
export function queryRedirectTarget(r: QueryRedirect, search: string): string {
  const from = new URLSearchParams(search);
  const out = r.keep === 'all' ? new URLSearchParams(from) : new URLSearchParams();
  if (r.keep !== 'all') {
    for (const key of r.keep) {
      for (const value of from.getAll(key)) out.append(key, value);
    }
  }
  for (const [key, value] of Object.entries(r.set ?? {})) out.set(key, value);
  const q = out.toString();
  return q ? `${r.to}?${q}` : r.to;
}

/** The badge label for a count, or null when there is nothing to flag. */
export function badgeText(count: number | undefined): string | null {
  if (count === undefined || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

/**
 * The case's display name (crumb, palette): the building's record name when
 * the project metadata has one, else the `.rux` file name from `/health`.
 */
export function displayProjectName(
  summary: ProjectSummary | undefined,
  health: Health | undefined,
): string | undefined {
  const recordName = summary?.projects?.[0]?.name?.trim();
  return recordName ? recordName : health?.project.name;
}

/** Storage seam shared with `cases.ts`. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
